/**
 * ABC Classification Utility
 *
 * Shared utility for calculating ABC classification based on annual value.
 * Used by both frontend and backend for consistent calculation.
 *
 * DRY Compliance: Single source of truth for ABC calculation
 * Rule 15 Compliance: Root cause solution - no scattered logic
 *
 * @module @axon/common/utils/abc-classification
 */

import { ABCClassification } from '../types';
import { ABC_THRESHOLDS } from '../constants';

/**
 * Calculate ABC classification based on annual value
 *
 * ABC Analysis is an inventory categorization method that divides items into
 * three categories based on their annual consumption value:
 *
 * - Class A: High value items (top 20% by value, typically ~80% of total inventory value)
 * - Class B: Medium value items (next 30% by value, typically ~15% of total inventory value)
 * - Class C: Low value items (bottom 50% by value, typically ~5% of total inventory value)
 *
 * @param unitPrice - The unit price of the product
 * @param annualQuantity - The annual consumption/sales quantity
 * @param thresholds - Optional custom thresholds (defaults to ABC_THRESHOLDS)
 * @returns The ABC classification or A, B, or C, or undefined if inputs are invalid
 *
 * @example
 * // High value product: $100 price, 1000 units/year = $100,000 annual value -> 'A'
 * calculateABCClassification(100, 1000) // Returns 'A'
 *
 * // Medium value product: $50 price  500 units/year = $25,000 annual value -> 'B'
 * calculateABCClassification(50, 500) // Returns 'B'
 *
 * // Low value product: $10 price  100 units/year = $1,000 annual value -> 'C'
 * calculateABCClassification(10, 100) // Returns 'C'
 */
export function calculateABCClassification(
  unitPrice: number,
  annualQuantity: number,
  thresholds: {
    CLASS_A_THRESHOLD: number;
    CLASS_B_THRESHOLD: number;
  } = ABC_THRESHOLDS
): ABCClassification | undefined {
  // Validate inputs - reject zero, negative, NaN, null, undefined
  if (!unitPrice || !annualQuantity || unitPrice <= 0 || annualQuantity <= 0) {
    return undefined;
  }

  // Calculate annual value
  const annualValue = unitPrice * annualQuantity;

  // Determine classification based on thresholds
  if (annualValue >= thresholds.CLASS_A_THRESHOLD) {
    return 'A';
  }

  if (annualValue >= thresholds.CLASS_B_THRESHOLD) {
    return 'B';
  }

  return 'C';
}

/**
 * Get ABC classification description
 *
 * @param classification - The ABC classification
 * @returns Human-readable description of the classification
 */
export function getABCClassificationDescription(
  classification: ABCClassification | undefined
): string {
  switch (classification) {
    case 'A':
      return 'High Value - Frequent monitoring recommended';
    case 'B':
      return 'Medium Value - Regular monitoring recommended';
    case 'C':
      return 'Low Value - Minimal monitoring sufficient';
    default:
      return 'Unclassified - No automatic classification applied';
  }
}

/**
 * Get ABC classification color (for UI display)
 *
 * @param classification - The ABC classification
 * @returns Color palette name for UI display
 */
export function getABCClassificationColor(classification: ABCClassification | undefined): string {
  switch (classification) {
    case 'A':
      return 'green';
    case 'B':
      return 'yellow';
    case 'C':
      return 'gray';
    default:
      return 'gray';
  }
}

// Re-export types needed
export type { ABCClassification } from '../types';
